import { useState, useEffect } from 'react';
import {
  Mail, Plus, Trash2, ToggleLeft, ToggleRight, ExternalLink,
  CheckCircle, AlertCircle, XCircle, Shield, RefreshCw,
  ChevronDown, ChevronUp, Flame, Play, Pause, Key, Eye, EyeOff
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const STAGE_EMAILS = [5, 10, 20, 40, 80];

function StatusIcon({ status }) {
  if (status === 'healthy') return <CheckCircle size={14} color="var(--green)" />;
  if (status === 'warning') return <AlertCircle size={14} color="var(--amber)" />;
  if (status === 'error')   return <XCircle size={14} color="var(--red)" />;
  return <Shield size={14} color="var(--text3)" />;
}

function CheckRow({ check }) {
  const bg     = { pass: 'rgba(34,197,94,0.06)', warn: 'rgba(245,158,11,0.06)', fail: 'rgba(239,68,68,0.06)' };
  const border = { pass: 'rgba(34,197,94,0.2)',  warn: 'rgba(245,158,11,0.2)',  fail: 'rgba(239,68,68,0.2)' };
  const colors = { pass: 'var(--green)', warn: 'var(--amber)', fail: 'var(--red)' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', borderRadius: 'var(--radius-sm)', background: bg[check.status], border: `1px solid ${border[check.status]}` }}>
      <StatusIcon status={check.status === 'pass' ? 'healthy' : check.status === 'warn' ? 'warning' : 'error'} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 500 }}>{check.name}</div>
        <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 1 }}>{check.message}</div>
      </div>
      <span style={{ fontSize: 10, fontWeight: 600, color: colors[check.status], textTransform: 'uppercase' }}>{check.status}</span>
    </div>
  );
}

export default function Accounts() {
  const [tab, setTab] = useState('gmail');
  const [accounts, setAccounts] = useState([]);
  const [brevoAccounts, setBrevoAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [checking, setChecking] = useState({});
  const [checkResults, setCheckResults] = useState({});
  const [expanded, setExpanded] = useState({});
  const [checkingAll, setCheckingAll] = useState(false);
  const [warmerStats, setWarmerStats] = useState([]);
  const [warming, setWarming] = useState(false);
  const [togglingWarmer, setTogglingWarmer] = useState({});

  // Brevo form
  const [showBrevoForm, setShowBrevoForm] = useState(false);
  const [brevoForm, setBrevoForm] = useState({ apiKey: '', senderName: '', senderEmail: '' });
  const [brevoValidating, setBrevoValidating] = useState(false);
  const [brevoValidated, setBrevoValidated] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [savingBrevo, setSavingBrevo] = useState(false);

  useEffect(() => {
    loadAccounts();
    loadBrevoAccounts();
    const params = new URLSearchParams(window.location.search);
    if (params.get('linked')) { toast.success(`${params.get('linked')} linked!`); window.history.replaceState({}, '', '/accounts'); }
    if (params.get('error'))  { toast.error('Failed to link account.'); window.history.replaceState({}, '', '/accounts'); }
  }, []);

  const loadAccounts = () => {
    api.get('/api/accounts').then(r => setAccounts(r.data.accounts || [])).finally(() => setLoading(false));
  };

  const loadBrevoAccounts = () => {
    api.get('/api/brevo').then(r => setBrevoAccounts(r.data.accounts || [])).catch(() => {});
  };

  const loadWarmerStats = () => {
    api.get('/api/accounts/warmer/stats').then(r => setWarmerStats(r.data.accounts || []));
  };

  useEffect(() => { if (tab === 'warmer') loadWarmerStats(); }, [tab]);

  const linkGmail = async () => {
    setLinking(true);
    try { const res = await api.get('/api/auth/google'); window.location.href = res.data.url; }
    catch { toast.error('Could not start Gmail connection'); setLinking(false); }
  };

  const toggleAccount = async (id) => {
    try {
      const res = await api.patch(`/api/accounts/${id}/toggle`);
      setAccounts(a => a.map(acc => acc.id === id ? { ...acc, is_active: res.data.account.is_active } : acc));
    } catch { toast.error('Failed to update'); }
  };

  const deleteAccount = async (id, email) => {
    if (!window.confirm(`Unlink ${email}?`)) return;
    try { await api.delete(`/api/accounts/${id}`); setAccounts(a => a.filter(acc => acc.id !== id)); toast.success('Account unlinked'); }
    catch { toast.error('Failed to unlink'); }
  };

  const checkAccount = async (id) => {
    setChecking(c => ({ ...c, [id]: true }));
    setExpanded(e => ({ ...e, [id]: true }));
    try {
      const res = await api.post(`/api/accounts/${id}/check`);
      setCheckResults(r => ({ ...r, [id]: res.data }));
      setAccounts(a => a.map(acc => acc.id === id ? { ...acc, last_check_status: res.data.status, last_checked_at: new Date().toISOString() } : acc));
      if (res.data.status === 'healthy') toast.success(`${res.data.email} — all checks passed!`);
      else if (res.data.status === 'warning') toast(`${res.data.email} — some warnings`, { icon: '⚠️' });
      else toast.error(`${res.data.email} — check failed`);
    } catch (err) { toast.error(err.response?.data?.error || 'Check failed'); }
    finally { setChecking(c => ({ ...c, [id]: false })); }
  };

  const checkAll = async () => {
    setCheckingAll(true);
    try {
      const checkingState = {};
      accounts.forEach(a => { checkingState[a.id] = true; });
      setChecking(checkingState);
      const res = await api.post('/api/accounts/check-all');
      const results = res.data.results || [];
      const newResults = {};
      results.forEach(r => {
        const acc = accounts.find(a => a.email === r.email);
        if (acc) newResults[acc.id] = r;
      });
      setCheckResults(prev => ({ ...prev, ...newResults }));
      const expandState = {};
      Object.keys(newResults).forEach(id => { expandState[id] = true; });
      setExpanded(expandState);
      setAccounts(a => a.map(acc => {
        const r = newResults[acc.id];
        return r ? { ...acc, last_check_status: r.status, last_checked_at: new Date().toISOString() } : acc;
      }));
      const healthy = results.filter(r => r.status === 'healthy').length;
      const errors  = results.filter(r => r.status === 'error').length;
      if (errors > 0) toast.error(`${errors} account(s) have errors`);
      else toast.success(`All ${healthy} accounts passed!`);
    } catch (err) { toast.error('Check failed'); }
    finally { setChecking({}); setCheckingAll(false); }
  };

  const toggleWarmer = async (id) => {
    setTogglingWarmer(t => ({ ...t, [id]: true }));
    try {
      const res = await api.post(`/api/accounts/${id}/warmer/toggle`);
      setWarmerStats(s => s.map(a => a.id === id ? { ...a, warmer_enabled: res.data.account.warmer_enabled } : a));
      toast.success(res.data.message);
    } catch (err) { toast.error('Failed'); }
    finally { setTogglingWarmer(t => ({ ...t, id: false })); }
  };

  const runWarmer = async () => {
    setWarming(true);
    try {
      const res = await api.post('/api/accounts/warmer/run');
      if (res.data.success) toast.success(`Warming done — ${res.data.sent} emails sent`);
      else toast.error(res.data.message);
      loadWarmerStats();
    } catch (err) { toast.error('Warming failed'); }
    finally { setWarming(false); }
  };

  const [checkingBrevo, setCheckingBrevo] = useState({});
  const [brevoStatuses, setBrevoStatuses] = useState({});

  const checkBrevoStatus = async (id) => {
    setCheckingBrevo(c => ({ ...c, [id]: true }));
    try {
      const res = await api.post(`/api/brevo/${id}/check`);
      setBrevoStatuses(s => ({ ...s, [id]: res.data }));
      if (res.data.status === 'active') toast.success('Account is active!');
      else if (res.data.status === 'suspended') {
        toast.error('Account suspended by Brevo!');
        setBrevoAccounts(a => a.map(acc => acc.id === id ? { ...acc, is_active: false } : acc));
      } else toast.error(res.data.message);
    } catch (err) { toast.error('Check failed'); }
    finally { setCheckingBrevo(c => ({ ...c, [id]: false })); }
  };

  const validateBrevo = async () => {
    if (!brevoForm.apiKey || !brevoForm.senderEmail) return toast.error('Enter API key and sender email');
    setBrevoValidating(true);
    setBrevoValidated(false);
    try {
      const res = await api.post('/api/brevo/validate', { apiKey: brevoForm.apiKey, senderEmail: brevoForm.senderEmail });
      setBrevoValidated(true);
      if (!brevoForm.senderName && res.data.senderName) {
        setBrevoForm(f => ({ ...f, senderName: res.data.senderName }));
      }
      toast.success(`✅ Valid! Connected to ${res.data.accountName}`);
    } catch (err) {
      const data = err.response?.data;
      if (data?.senderInvalid) {
        toast.error(data.error);
        setBrevoValidated(false);
      } else {
        toast.error(data?.error || 'Validation failed');
      }
    } finally { setBrevoValidating(false); }
  };

  const saveBrevoAccount = async () => {
    if (!brevoForm.apiKey || !brevoForm.senderName || !brevoForm.senderEmail) return toast.error('All fields required');
    setSavingBrevo(true);
    try {
      const res = await api.post('/api/brevo', brevoForm);
      setBrevoAccounts(a => [...a, res.data.account]);
      setBrevoForm({ apiKey: '', senderName: '', senderEmail: '' });
      setShowBrevoForm(false);
      setBrevoValidated(false);
      toast.success('Brevo account added!');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to add'); }
    finally { setSavingBrevo(false); }
  };

  const deleteBrevoAccount = async (id, email) => {
    if (!window.confirm(`Remove Brevo account ${email}?`)) return;
    await api.delete(`/api/brevo/${id}`);
    setBrevoAccounts(a => a.filter(x => x.id !== id));
    toast.success('Removed');
  };

  const overallBadge = (status) => {
    const map = { healthy: 'badge-green', warning: 'badge-amber', error: 'badge-red' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status || 'not checked'}</span>;
  };

  const tabs = [
    { key: 'gmail',     label: 'Gmail accounts',     icon: <Mail size={13} /> },
    { key: 'brevo',     label: 'Brevo accounts',     icon: <Key size={13} /> },
    { key: 'integrity', label: 'Integrity checker',  icon: <Shield size={13} /> },
    { key: 'warmer',    label: 'Email warmer',        icon: <Flame size={13} /> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.4px' }}>Email accounts</h1>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 3 }}>Manage Gmail and Brevo sending accounts</p>
        </div>
        {tab === 'gmail' && (
          <button className="btn btn-primary" onClick={linkGmail} disabled={linking}>
            <Plus size={14} /> {linking ? 'Connecting…' : 'Link Gmail'} <ExternalLink size={12} />
          </button>
        )}
        {tab === 'brevo' && (
          <button className="btn btn-primary" onClick={() => setShowBrevoForm(!showBrevoForm)}
            style={{ background: 'var(--amber)', borderColor: 'var(--amber)' }}>
            <Plus size={14} /> Add Brevo account
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none',
            color: tab === t.key ? 'var(--text)' : 'var(--text2)',
            fontSize: 13, fontWeight: tab === t.key ? 500 : 400,
            padding: '8px 14px',
            borderBottom: tab === t.key ? '2px solid var(--blue)' : '2px solid transparent',
            marginBottom: -1, cursor: 'pointer',
          }}>{t.icon}{t.label}</button>
        ))}
      </div>

      {/* ── GMAIL TAB ── */}
      {tab === 'gmail' && (
        <div className="card">
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
            {accounts.length} linked Gmail {accounts.length === 1 ? 'account' : 'accounts'}
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
          ) : accounts.length === 0 ? (
            <div style={{ padding: 56, textAlign: 'center' }}>
              <Mail size={36} color="var(--text3)" style={{ marginBottom: 14 }} />
              <p style={{ color: 'var(--text2)', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>No Gmail accounts linked yet</p>
              <button className="btn btn-primary" onClick={linkGmail}><Plus size={14} /> Link Gmail</button>
            </div>
          ) : accounts.map((acc, i) => (
            <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: i < accounts.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: acc.is_active ? 'var(--blue-dim)' : 'var(--bg3)', color: acc.is_active ? '#93c5fd' : 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14, flexShrink: 0 }}>
                {acc.email.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{acc.email}</span>
                  {acc.warmer_enabled && <span className="badge badge-amber"><Flame size={9} /> warming</span>}
                  {!acc.is_active && <span className="badge badge-gray">disabled</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>Queue #{i + 1} · {acc.daily_sent_count || 0} sent today</div>
              </div>
              <div style={{ width: 90 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>
                  <span>Daily</span><span>{acc.daily_sent_count || 0}/500</span>
                </div>
                <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 20, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 20, background: 'var(--blue)', width: `${Math.min(((acc.daily_sent_count || 0) / 500) * 100, 100)}%` }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => toggleAccount(acc.id)}>
                  {acc.is_active ? <ToggleRight size={15} color="var(--blue)" /> : <ToggleLeft size={15} />}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => deleteAccount(acc.id, acc.email)}>
                  <Trash2 size={13} color="var(--red)" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── BREVO TAB ── */}
      {tab === 'brevo' && (
        <div>
          {/* Guide */}
          <div style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(251,191,36,0.04))', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius)', padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Key size={15} color="var(--amber)" /> How to add a Brevo account
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                { num: '1', title: 'Create account', desc: 'Go to brevo.com and sign up free. 300 emails/day included.' },
                { num: '2', title: 'Verify sender', desc: 'Go to Senders & IP → Senders → Add your email → verify it.' },
                { num: '3', title: 'Get API key', desc: 'Click your name → SMTP & API → API Keys → Generate new key.' },
                { num: '4', title: 'Add to Bulkify', desc: 'Click "Add Brevo account" and paste your API key here.' },
              ].map(step => (
                <div key={step.num} style={{ background: 'var(--bg2)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--amber)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, marginBottom: 8 }}>{step.num}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{step.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>{step.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text2)' }}>
              💡 <strong>Why Brevo?</strong> Emails sent via Brevo land in inbox much more reliably than Gmail. Add multiple Brevo accounts to increase your daily sending limit.
              <a href="https://brevo.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--amber)', marginLeft: 8, textDecoration: 'none' }}>Open Brevo →</a>
            </div>
          </div>

          {/* Add Brevo form */}
          {showBrevoForm && (
            <div className="card" style={{ padding: 22, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 18 }}>Add Brevo account</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
                    Brevo API Key <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input className="input" type={showKey ? 'text' : 'password'} placeholder="xkeysib-xxxxxxxxxxxxxxxx"
                      value={brevoForm.apiKey} onChange={e => { setBrevoForm(f => ({ ...f, apiKey: e.target.value })); setBrevoValidated(false); }}
                      style={{ paddingRight: 40 }} />
                    <button type="button" onClick={() => setShowKey(!showKey)}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer' }}>
                      {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Get this from Brevo → your name → SMTP & API → API Keys</div>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
                    Sender email <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input className="input" type="email" placeholder="you@example.com"
                    value={brevoForm.senderEmail} onChange={e => { setBrevoForm(f => ({ ...f, senderEmail: e.target.value })); setBrevoValidated(false); }} />
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Must be verified in Brevo → Senders & IP → Senders</div>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
                    Sender name <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input className="input" placeholder="John from Meshcode" value={brevoForm.senderName}
                    onChange={e => setBrevoForm(f => ({ ...f, senderName: e.target.value }))} />
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>This is the name recipients see in their inbox</div>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {!brevoValidated ? (
                    <button className="btn btn-ghost" onClick={validateBrevo} disabled={brevoValidating}>
                      <Shield size={14} /> {brevoValidating ? 'Validating…' : 'Validate API key'}
                    </button>
                  ) : (
                    <button className="btn btn-primary" onClick={saveBrevoAccount} disabled={savingBrevo}>
                      <CheckCircle size={14} /> {savingBrevo ? 'Saving…' : 'Save account'}
                    </button>
                  )}
                  <button className="btn btn-ghost" onClick={() => { setShowBrevoForm(false); setBrevoValidated(false); setBrevoForm({ apiKey: '', senderName: '', senderEmail: '' }); }}>Cancel</button>
                  {brevoValidated && <span style={{ fontSize: 12, color: 'var(--green)', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={12} /> API key verified!</span>}
                </div>
              </div>
            </div>
          )}

          {/* Brevo accounts list */}
          <div className="card">
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 500 }}>
              {brevoAccounts.length} Brevo {brevoAccounts.length === 1 ? 'account' : 'accounts'}
              <span style={{ fontSize: 11, color: 'var(--text3)', marginLeft: 10 }}>
                {brevoAccounts.filter(a => a.is_active).length * 300} emails/day total capacity
              </span>
            </div>
            {brevoAccounts.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <Key size={32} color="var(--text3)" style={{ marginBottom: 12 }} />
                <p style={{ color: 'var(--text2)', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>No Brevo accounts yet</p>
                <p style={{ color: 'var(--text3)', fontSize: 12, marginBottom: 18 }}>Add a Brevo account for better email deliverability</p>
                <button className="btn btn-primary" onClick={() => setShowBrevoForm(true)} style={{ background: 'var(--amber)', borderColor: 'var(--amber)' }}>
                  <Plus size={14} /> Add Brevo account
                </button>
              </div>
            ) : brevoAccounts.map((acc, i) => (
              <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: i < brevoAccounts.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: acc.is_active ? 'rgba(245,158,11,0.15)' : 'var(--bg3)', color: acc.is_active ? 'var(--amber)' : 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14, flexShrink: 0 }}>
                  {acc.sender_email.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 500, fontSize: 13 }}>{acc.sender_email}</span>
                    <span className="badge badge-amber">Brevo</span>
                    {!acc.is_active && <span className="badge badge-red">⚠️ Suspended/Disabled</span>}
                    {brevoStatuses[acc.id]?.status === 'active' && <span className="badge badge-green">✓ Active</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                    {acc.sender_name} · {acc.daily_sent_count || 0}/300 sent today · Queue #{i + 1}
                    {!acc.is_active && <span style={{ color: 'var(--red)', marginLeft: 8 }}>— Remove this account and create a new Brevo account</span>}
                  </div>
                </div>
                <div style={{ width: 90 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>
                    <span>Daily</span><span>{acc.daily_sent_count || 0}/300</span>
                  </div>
                  <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 20, overflow: 'hidden' }}>
                    <div style={{ height: '100%', borderRadius: 20, background: 'var(--amber)', width: `${Math.min(((acc.daily_sent_count || 0) / 300) * 100, 100)}%` }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => checkBrevoStatus(acc.id)} disabled={checkingBrevo[acc.id]}>
                    {checkingBrevo[acc.id] ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Checking…</> : <><Shield size={12} /> Check</>}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => deleteBrevoAccount(acc.id, acc.sender_email)}>
                    <Trash2 size={13} color="var(--red)" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── INTEGRITY TAB ── */}
      {tab === 'integrity' && (
        <div>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--blue)', borderRadius: 'var(--radius)', padding: '14px 18px', marginBottom: 16, fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text)' }}>Email integrity check</strong> — verifies each Gmail account's token, access, daily limits, reputation and warmer status.
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={checkAll} disabled={checkingAll}>
              <Shield size={14} /> {checkingAll ? 'Checking all…' : 'Check all accounts'}
            </button>
          </div>
          <div className="card">
            {accounts.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No Gmail accounts linked yet</div>
            ) : accounts.map((acc, i) => {
              const result = checkResults[acc.id];
              const isOpen = expanded[acc.id];
              const isChecking = checking[acc.id];
              const status = result?.status || acc.last_check_status;
              return (
                <div key={acc.id} style={{ borderBottom: i < accounts.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px' }}>
                    <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--bg3)', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
                      {acc.email.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 500, fontSize: 13 }}>{acc.email}</span>
                        {status ? overallBadge(status) : <span className="badge badge-gray">Not checked</span>}
                      </div>
                      {acc.last_checked_at && (
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                          Last checked {new Date(acc.last_checked_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => checkAccount(acc.id)} disabled={isChecking}>
                        {isChecking ? <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Checking…</> : <><Shield size={12} /> Run check</>}
                      </button>
                      {result && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setExpanded(e => ({ ...e, [acc.id]: !isOpen }))}>
                          {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        </button>
                      )}
                    </div>
                  </div>
                  {result && isOpen && (
                    <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {result.checks.map((check, ci) => <CheckRow key={ci} check={check} />)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── WARMER TAB ── */}
      {tab === 'warmer' && (
        <div>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--amber)', borderRadius: 'var(--radius)', padding: '14px 18px', marginBottom: 16, fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--text)' }}>Email warmer</strong> — sends emails between your linked Gmail accounts daily to build up sending reputation. Warm for 2-4 weeks before blasting campaigns.
          </div>
          <div className="card" style={{ padding: 18, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Warming stages</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {[{s:1,e:5,l:'Week 1',c:'#60a5fa'},{s:2,e:10,l:'Week 2',c:'#34d399'},{s:3,e:20,l:'Week 3',c:'#fbbf24'},{s:4,e:40,l:'Week 4',c:'#f97316'},{s:5,e:80,l:'Week 5+',c:'#a78bfa'}].map(s => (
                <div key={s.s} style={{ background: 'var(--bg3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', textAlign: 'center', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: s.c }}>{s.e}/day</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>Stage {s.s} · {s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={runWarmer} disabled={warming} style={{ background: 'var(--amber)', borderColor: 'var(--amber)' }}>
              <Flame size={14} /> {warming ? 'Warming…' : 'Run warming cycle now'}
            </button>
          </div>
          <div className="card">
            {warmerStats.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No accounts loaded</div>
            ) : warmerStats.map((acc, i) => {
              const stage = acc.warmer_stage || 1;
              return (
                <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: i < warmerStats.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: acc.warmer_enabled ? 'rgba(245,158,11,0.15)' : 'var(--bg3)', color: acc.warmer_enabled ? 'var(--amber)' : 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
                    {acc.warmer_enabled ? <Flame size={16} /> : acc.email.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontWeight: 500, fontSize: 13 }}>{acc.email}</span>
                      {acc.warmer_enabled ? <span className="badge badge-amber"><Flame size={9} /> Stage {stage} · {[5,10,20,40,80][stage-1]}/day</span> : <span className="badge badge-gray">Warmer off</span>}
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>
                        <span>Stage {stage} of 5</span><span>{acc.warmer_emails_today || 0} sent today</span>
                      </div>
                      <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 20, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 20, background: 'var(--amber)', width: `${(stage/5)*100}%`, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                  </div>
                  <button className={`btn btn-sm ${acc.warmer_enabled ? 'btn-ghost' : 'btn-primary'}`}
                    style={acc.warmer_enabled ? { borderColor: 'var(--amber)', color: 'var(--amber)' } : { background: 'var(--amber)', borderColor: 'var(--amber)' }}
                    onClick={() => toggleWarmer(acc.id)} disabled={togglingWarmer[acc.id]}>
                    {acc.warmer_enabled ? <><Pause size={12} /> Stop warming</> : <><Play size={12} /> Start warming</>}
                  </button>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text3)', textAlign: 'center' }}>
            Needs at least 2 accounts with warmer enabled. Warming sends emails between your own accounts only.
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
